export type VariablePageSizeProps = {
  form: HTMLFormElement;
  loadAllOnClick?: boolean;
  loadMoreButtonId: string;
  pageSizeXs: number;
  pageSizeSm: number;
  pageSizeMd: number;
  pageSizeLg: number;
  pageSizeXl: number;
  searchResultsSelector: string;
  serverPageSize: number;
};

/**
 * Initializes a variable page size per breakpoint for search results. Assumes that paging
 * is done through a "load more button".
 */
export const initializeVariablePageSize = ({
  form,
  loadAllOnClick,
  loadMoreButtonId,
  pageSizeXs,
  pageSizeSm,
  pageSizeMd,
  pageSizeLg,
  pageSizeXl,
  searchResultsSelector,
  serverPageSize
}: VariablePageSizeProps) => {
  let maxNumVisibleItems = pageSizeXl;
  let pageSize = pageSizeXl;
  let serverPageNumber = 1;

  // Hides search results according to how many should be visible at the moment
  const updateVisiblePagedItems = () => {
    // Refresh searchResultsList here as it's replaced by HTMX
    const searchResultsList = document.querySelector<HTMLElement>(searchResultsSelector);

    if (searchResultsList) {
      for (let i = 0; i < searchResultsList.childElementCount; ++i) {
        const child = searchResultsList.children[i] as HTMLElement;

        child.style.display = i < maxNumVisibleItems ? "" : "none";
      }
    }
  };

  // Updates the "load more button"'s text and title, and its visibility.
  const updateLoadMoreButton = () => {
    // Refresh the load more button here as it gets replaced by HTMX
    const loadMoreButton = document.getElementById(loadMoreButtonId);

    if (loadMoreButton) {
      const totalHits = loadMoreButton.dataset.totalHits
        ? parseInt(loadMoreButton.dataset.totalHits)
        : undefined;

      if (totalHits !== undefined) {
        let itemsRemaining = Math.max(0, totalHits - maxNumVisibleItems);

        itemsRemaining = Math.min(itemsRemaining, pageSize);

        if (itemsRemaining > 0) {
          if (!loadAllOnClick) {
            const labelTemplate = loadMoreButton.dataset.labelTemplate;

            const contentElement = loadMoreButton.querySelector(
              "[data-load-more-button-content]"
            );

            if (labelTemplate && contentElement) {
              loadMoreButton.title = labelTemplate.replace("{0}", `${itemsRemaining}`);
              contentElement.innerHTML = labelTemplate.replace(
                "{0}",
                `${itemsRemaining}`
              );
            }
          }

          loadMoreButton.style.display = "";
        } else {
          loadMoreButton.style.display = "none";
        }
      }
    }
  };

  // Note: We need to use afterSettle here in case the "load more button" does a full swap (ie not OOB swap) of itself.
  // This is due to an "attribute swapping" gotcha which can be read about here: https://htmx.org/docs/#details.
  // In short, if we use afterOnLoad or afterSwap here instead of afterSettle then any new attributes we set on the
  // button (eg title, style) would be replaced/removed after the settle event, which is not what we want.
  htmx.on(form, "htmx:afterSettle", e => {
    const event = e as AfterSettleEvent;

    // Note: We use detail.requestConfig.elt to figure out the triggering element, and not
    // detail.elt which doesn't work.
    if (event.detail.requestConfig.elt.id !== loadMoreButtonId) {
      // Reset paging when filtering
      maxNumVisibleItems = pageSize;
      serverPageNumber = 1;
    }

    updateVisiblePagedItems();

    updateLoadMoreButton();
  });

  htmx.on(form, "htmx:confirm", e => {
    const event = e as ConfirmEvent;

    if (event.detail.elt.id === loadMoreButtonId) {
      if (loadAllOnClick) {
        maxNumVisibleItems = 1000;
      } else {
        maxNumVisibleItems = maxNumVisibleItems + pageSize;
      }

      if (maxNumVisibleItems > serverPageNumber * serverPageSize) {
        // Client side paging exceeds the number of items retrieved from the server.
        // Don't cancel the request making sure we fetch a new page from the server.
        serverPageNumber = serverPageNumber + 1;
      } else {
        // Cancel request and only do client side paging
        e.preventDefault();

        updateVisiblePagedItems();

        updateLoadMoreButton();
      }
    }
  });

  const updatePageSize = () => {
    if (window.innerWidth < 640) {
      pageSize = pageSizeXs;
    } else if (window.innerWidth < 1024) {
      pageSize = pageSizeSm;
    } else if (window.innerWidth < 1440) {
      pageSize = pageSizeMd;
    } else if (window.innerWidth < 1920) {
      pageSize = pageSizeLg;
    } else {
      pageSize = pageSizeXl;
    }
  };

  window.addEventListener("resize", () => {
    updatePageSize();

    if (maxNumVisibleItems < pageSize) {
      maxNumVisibleItems = pageSize;
    }

    updateVisiblePagedItems();

    updateLoadMoreButton();
  });

  // Set initial state
  updatePageSize();

  maxNumVisibleItems = pageSize;

  updateVisiblePagedItems();

  updateLoadMoreButton();
};
